import LiveFeedback from './LiveFeedback';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../../../../Component/axios_Interceptor/api';
import url from '../../../../Component/server';
import { sellerNoticeUpdated, SellerNotice, useSellerChat } from '../../../Broadcast/sellerNotice';
import { handleChatComposerKeyDown } from '../../../../Component/Support/chatComposerKeyboard';

export default function SellerChatComposer({ liveSeq }: { liveSeq: number }) {
  const load = useCallback(async () => (await api.get(url.Communication_url.get_live_notice, { params: { seq: liveSeq, type: 'sn' } })).data as SellerNotice[], [liveSeq]);
  const { messages, loading, error: loadError } = useSellerChat(liveSeq, load);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const running = useRef(false);
  const input = useRef<HTMLTextAreaElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  useEffect(() => { if (atBottom.current && list.current) list.current.scrollTop = list.current.scrollHeight; }, [messages]);
  const send = async (event: FormEvent) => {
    event.preventDefault();
    const content = draft.trim();
    if (!content || running.current) return;
    input.current?.focus({ preventScroll: true });
    running.current = true; setSending(true); setError('');
    try {
      const result = await api.post(url.Communication_url.create_live_notice, { live_seq: liveSeq, type: 'sn', content });
      if (result.data !== '성공') throw new Error('Send failed');
      setDraft(''); atBottom.current = true; sellerNoticeUpdated(liveSeq);
    } catch { setError('셀러 채팅을 전송하지 못했습니다. 다시 시도해주세요.'); }
    finally { running.current = false; setSending(false); }
  };
  return <Panel aria-label="셀러 채팅 작성">
    <div ref={list} className="seller-chat-messages" role="log" aria-label="셀러 채팅 내역" onScroll={event => {
      const node = event.currentTarget; atBottom.current = node.scrollHeight - node.clientHeight - node.scrollTop < 12;
    }}>
      {loading ? <p role="status">셀러 채팅을 불러오는 중입니다.</p> : messages.length === 0 ? <p className="empty">셀러에게 전달할 채팅을 입력해주세요.</p> : messages.map(message =>
        <p key={message.seq}><span>{message.content}</span>{message.crt_dt && <time>{message.crt_dt.slice(11, 16)}</time>}</p>)}
    </div>
    {(error || loadError) && <LiveFeedback role="alert">{error || loadError} {loadError && <button type="button" onClick={() => sellerNoticeUpdated(liveSeq)}>다시 불러오기</button>}</LiveFeedback>}
    <form aria-label="셀러 채팅 전송" onSubmit={send}>
      <textarea ref={input} aria-label="셀러 채팅 메시지" placeholder="셀러 채팅을 입력해주세요." rows={Math.min(3, draft.split('\n').length)} value={draft} maxLength={4000} readOnly={sending} onChange={event => setDraft(event.target.value)}
        onKeyDown={event => handleChatComposerKeyDown(event, setDraft, () => { void send(event); })} />
      <button type="submit" style={{ height: 44, minHeight: 44 }} disabled={sending || !draft.trim()}>{sending ? '전송 중' : '전송'}</button>
    </form>
  </Panel>;
}

const Panel = styled.section`
  min-width: 0;
  .seller-chat-messages { height: 280px; overflow-y: auto; overscroll-behavior: contain; padding: 12px; border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-muted); }
  .seller-chat-messages p { display: flex; align-items: flex-start; gap: 12px; margin: 0 0 16px; line-height: 1.5; }
  .seller-chat-messages span { flex: 1; min-width: 0; white-space: pre-wrap; overflow-wrap: anywhere; color: var(--color-warning, #ffcc00); }
  .seller-chat-messages time { flex-shrink: 0; color: var(--color-text-secondary); font-size: var(--text-caption); }
  .seller-chat-messages .empty { color: var(--color-text-secondary); }
  form { display: flex; align-items: stretch; gap: 8px; margin-top: 12px; }
  form textarea { flex: 1; min-width: 0; width: auto; resize: none; min-height: 44px; box-sizing: border-box; padding: var(--space-2) var(--space-3); border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-muted); color: var(--color-text-primary); font: inherit; line-height: 22px; }
  && form button { display: flex; align-items: center; justify-content: center; height: 44px; min-height: 44px; box-sizing: border-box; padding: 0 16px; border: 1px solid var(--color-brand); border-radius: var(--radius-md); background: var(--color-brand); color: var(--color-text-on-brand); font: inherit; cursor: pointer; }
  && form button:hover:not(:disabled) { background: var(--color-brand-hover); }
  && form button:disabled { opacity: 0.5; cursor: default; }
  form :focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;
